import { AbstractControl, ValidationErrors } from '@angular/forms';

/**
 * El RUT chileno en el navegador.
 *
 * Es la misma lógica que corre en el backend (`src/common/rut/rut.ts`), a
 * propósito duplicada: el backend valida porque no puede confiar en nadie, y
 * el formulario valida para decirlo en el momento en que se escribe, en vez
 * de después de apretar "Crear" y esperar la respuesta.
 *
 * Si alguna vez cambia el algoritmo, hay que cambiarlo en los dos lados —pero
 * el módulo 11 del RUT no ha cambiado desde que existe.
 */

export function normalizarRut(valor: string): string {
  return (valor ?? '').replace(/[^0-9kK]/g, '').toUpperCase();
}

export function digitoVerificador(numero: string): string {
  let suma = 0;
  let multiplicador = 2;

  for (let i = numero.length - 1; i >= 0; i--) {
    suma += Number(numero[i]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }

  const resto = 11 - (suma % 11);
  if (resto === 11) return '0';
  if (resto === 10) return 'K';
  return String(resto);
}

export function esRutValido(valor: string): boolean {
  const limpio = normalizarRut(valor);
  if (limpio.length < 8 || limpio.length > 9) return false;

  const numero = limpio.slice(0, -1);
  const dv = limpio.slice(-1);

  if (!/^\d+$/.test(numero)) return false;
  if (numero.length < 7 || numero.length > 8) return false;

  return digitoVerificador(numero) === dv;
}

/** "123456785" -> "12.345.678-5". Para mostrar mientras se escribe. */
export function formatearRut(valor: string): string {
  const limpio = normalizarRut(valor);
  if (limpio.length < 2) return limpio;

  const numero = limpio.slice(0, -1);
  const dv = limpio.slice(-1);
  return `${numero.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv}`;
}

/**
 * "123456785" -> "12345678-5". Sin puntos, con guion: como se escribe en el
 * campo y como lo guarda el backend. Se recalcula desde el número limpio, así
 * que borrar el guion lo vuelve a poner en su lugar con la siguiente tecla.
 */
export function rutConGuion(valor: string): string {
  const limpio = normalizarRut(valor);
  if (limpio.length < 2) return limpio;
  return `${limpio.slice(0, -1)}-${limpio.slice(-1)}`;
}

/**
 * Validador para formularios reactivos.
 *
 * El campo vacío no es "inválido": de eso se encarga `Validators.required`,
 * y marcar en rojo un campo que la persona todavía no llenó es decirle que
 * se equivocó antes de que haga nada.
 */
export function validadorDeRut(control: AbstractControl): ValidationErrors | null {
  const valor = (control.value ?? '').toString();
  if (!valor.trim()) return null;
  return esRutValido(valor) ? null : { rut: true };
}
